/** Inventory & expiry risk tab - products approaching expiry, low usage velocity, overstocked
 * products, sites holding excessive stock, stock rotation compliance / FIFO exceptions, and
 * recommended transfer / markdown / donation actions. */

import { Box, Chip, Grid, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Section } from '../../../components/ui/Section';
import { EXPIRY_RISK, SITE_STOCK_RISK } from '../data';
import { StatusChip } from './StatusChip';

const InventoryExpiryTab = () => {
  const { t } = useTranslation('common');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardExpiryRiskTitle', 'Products approaching expiry / repeatedly wasted')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('wasteDashboardProduct', 'Product')}</TableCell>
                <TableCell>{t('wasteDashboardSite', 'Site')}</TableCell>
                <TableCell align="right">{t('wasteDashboardDaysToExpiry', 'Days to expiry')}</TableCell>
                <TableCell align="right">{t('wasteDashboardQuantityOnHand', 'Qty on hand')}</TableCell>
                <TableCell>{t('wasteDashboardUsageVelocity', 'Usage velocity')}</TableCell>
                <TableCell>{t('wasteDashboardFlags', 'Flags')}</TableCell>
                <TableCell>{t('wasteDashboardRecommendedAction', 'Recommended action')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {EXPIRY_RISK.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.product}</TableCell>
                  <TableCell>{row.site}</TableCell>
                  <TableCell align="right">
                    <StatusChip status={row.daysToExpiry <= 1 ? 'critical' : 'warning'}
                      label={`${row.daysToExpiry}d`} />
                  </TableCell>
                  <TableCell align="right">{row.quantityOnHand}</TableCell>
                  <TableCell>{row.usageVelocity}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {row.overstocked && <Chip size="small"
                        label={t('wasteDashboardOverstocked', 'Overstocked')} />}
                      {row.repeatedlyWasted && <Chip size="small"
                        color="warning"
                        label={t('wasteDashboardRepeatedlyWasted', 'Repeatedly wasted')} />}
                      {row.fifoException && <Chip size="small"
                        color="error"
                        label={t('wasteDashboardFifoException', 'FIFO exception')} />}
                    </Box>
                  </TableCell>
                  <TableCell>{row.recommendedAction}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textMD"
            fontWeight={600}
            sx={{ mb: 3 }}>
            {t('wasteDashboardSiteStockRiskTitle', 'Sites holding excessive stock / rotation compliance')}
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('wasteDashboardSite', 'Site')}</TableCell>
                <TableCell>{t('wasteDashboardExcessiveStockCategories', 'Excessive stock categories')}</TableCell>
                <TableCell align="right">{t('wasteDashboardRotationCompliance', 'Rotation compliance')}</TableCell>
                <TableCell align="right">{t('wasteDashboardFifoExceptions', 'FIFO exceptions')}</TableCell>
                <TableCell>{t('wasteDashboardRecommendedTransfer', 'Recommended transfer')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {SITE_STOCK_RISK.map((row) => (
                <TableRow key={row.site}>
                  <TableCell>{row.site}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {row.excessiveStockCategories.map((cat) => <Chip key={cat}
                        size="small"
                        label={cat} />)}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <StatusChip status={row.stockRotationCompliancePercent >= 80 ? 'good' : row.stockRotationCompliancePercent >= 70 ? 'warning' : 'critical'}
                      label={`${row.stockRotationCompliancePercent}%`} />
                  </TableCell>
                  <TableCell align="right">{row.fifoExceptions}</TableCell>
                  <TableCell>{row.recommendedTransferTo ?? t('wasteDashboardNoTransferNeeded', 'No transfer needed')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Section>

      <Grid container
        spacing={4}>
        <Grid item
          xs={12}
          md={6}>
          <Section style={{ height: '100%' }}>
            <Box sx={{ p: 4 }}>
              <Typography variant="textSM"
                fontWeight={600}>{t('wasteDashboardExpiryRiskByCategory', 'Expiry risk by category (highest exposure)')}</Typography>
              <Typography variant="textSM"
                color="text.secondary"
                sx={{ mt: 1, display: 'block' }}>
                {t('wasteDashboardExpiryRiskByCategoryNote', 'Fresh Produce and Fish & Seafood carry the shortest average shelf life and the highest count of expiry-risk items this period.')}
              </Typography>
            </Box>
          </Section>
        </Grid>
        <Grid item
          xs={12}
          md={6}>
          <Section style={{ height: '100%' }}>
            <Box sx={{ p: 4 }}>
              <Typography variant="textSM"
                fontWeight={600}>{t('wasteDashboardFifoExceptionsNote', 'First-expiry-first-out exceptions')}</Typography>
              <Typography variant="textSM"
                color="text.secondary"
                sx={{ mt: 1, display: 'block' }}>
                {t('wasteDashboardFifoExceptionsDetail', '20 FIFO exceptions recorded across all sites this period, concentrated at Stadium Hospitality Suite and Airport Terminal 2 Kitchen.')}
              </Typography>
            </Box>
          </Section>
        </Grid>
      </Grid>
    </Box>
  );
};

export default InventoryExpiryTab;
